<script setup lang="ts">
import ScrollToTop from "@core/components/ScrollToTop.vue";
import initCore from "@core/initCore";
import { initConfigStore, useConfigStore } from "@core/stores/config";
import { hexToRgb } from "@core/utils/colorConverter";
import { onBeforeUnmount, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { useTheme } from "vuetify";
import bus from "./kibi/core/utils/eventBus";
import { useAuthStore } from "./kibi/modules/auth/domain/store/auth_store";

const { global } = useTheme();
const authStore = useAuthStore();
const toast = useToast();
const { t } = useI18n();
const router = useRouter();
// ℹ️ Sync current theme with initial loader theme
initCore();
initConfigStore();

const configStore = useConfigStore();
const handleApiError = async (payload: any) => {
  const { status, error } = payload;
  if (status === 403) {
    if (error === "email_verification_required") {
      toast.warning(t("profile.messages.email_verification_required"));
      router.push("/profile");
    }
    if (error === "platform_access_denied") {
      toast.warning(t("profile.messages.account_access_denied"));
      router.push({ path: "/auth/acess-denied", query: { error } });
    }
  }
  if (status === 401 && error === "logout") {
    authStore.user = null;
    authStore.token = null;
    router.push("/auth/login");
  }
};
onBeforeUnmount(() => {
  bus.off("api_error", handleApiError);
});
onMounted(async () => {
  bus.on("api_error", handleApiError);

  // Refresh user data from server if authenticated
  if (authStore.token) {
    try {
      await authStore.fetchMe();
    } catch {
      // If fetching user fails (e.g., token expired), the API interceptor will handle logout
    }
  }
});
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp
      :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`"
    >
      <RouterView />

      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>
